import { useNavigate, useParams } from "react-router-dom";
import { buildAdminUserNavigationState, buildCompanyInfoPath, PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../util/helper";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Descriptions, Divider, Flex, List, Modal, Row, Space, Spin, Table } from "antd";
import Column from "antd/es/table/Column";
import type { TablePaginationConfig } from "antd/es/table";
import { BarcodeOutlined, CaretRightFilled, DatabaseOutlined, DownloadOutlined, EditOutlined, LeftOutlined, PauseOutlined, ProfileOutlined, ReconciliationOutlined, SlidersOutlined, UserAddOutlined } from "@ant-design/icons";
import { parse as parseContentDisposition } from "@tinyhttp/content-disposition";
import { CompanyContext, UpdateCompanyContext } from "../../../context/companyContext";
import { UpdateUsersContext, UserResponseType } from "../../../context/userContext";
import type { DepartmentResponseType } from "../../../context/departmentContext";
import { clientFetch } from "../../../utils/clientFetch";
import { notifyApiError, notifyHttpError } from "../../../utils/apiErrorHandler";
import { parseCsvErrorPayload, showCsvUploadErrorModal } from "../../../utils/csvErrorModal";
import { useTranslation } from "react-i18next";
import { CsvImportModalButton } from "../../../components/common/CsvImportModalButton";

const DEPARTMENT_PAGE_SIZE = 5;
const USER_PAGE_SIZE = 5;
type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

type UserMetaState = {
  active: number;
};

export function CompanyInfo() {
  const { t } = useTranslation();
  const { company } = useContext(CompanyContext);
  const { refreshCompany, toggleCompanyStatus } = useContext(UpdateCompanyContext);
  const { toggleUserDisabled } = useContext(UpdateUsersContext);
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [departmentList, setDepartmentList] = useState<Partial<DepartmentResponseType>[]>([]);
  const [departmentPagination, setDepartmentPagination] = useState<PaginationState>({ current: 1, pageSize: DEPARTMENT_PAGE_SIZE, total: 0 });
  const [userList, setUserList] = useState<UserResponseType[]>([]);
  const [userPagination, setUserPagination] = useState<PaginationState>({ current: 1, pageSize: USER_PAGE_SIZE, total: 0 });
  const [userMeta, setUserMeta] = useState<UserMetaState>({ active: 0 });
  const [currentAntdSortField, setCurrentAntdSortField] = useState<string | string[] | undefined>(undefined);
  const [currentAntdSortOrder, setCurrentAntdSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
  const [currentDepartmentSortOrder, setCurrentDepartmentSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
  const [currentDepartmentSortField, setCurrentDepartmentSortField] = useState<string | undefined>(undefined);

  const [isLoadingCompany, setIsLoadingCompany] = useState<boolean>(true);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState<boolean>(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingUsersCsv, setIsDownloadingUsersCsv] = useState(false);
  const [isPageReady, setIsPageReady] = useState(false);

  const fetchDepartments = useCallback(async (page?: number, pageSize?: number, sortField?: string, sortOrder?: 'asc' | 'desc') => {
    if (!companyId) {
      setDepartmentList([]);
      setDepartmentPagination({ current: 1, pageSize: pageSize ?? DEPARTMENT_PAGE_SIZE, total: 0 });
      setIsLoadingDepartments(false);
      return;
    }

    setIsLoadingDepartments(true);

    const targetPage = page ?? 1;
    const targetPageSize = pageSize ?? DEPARTMENT_PAGE_SIZE;

    try {
      let url = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/company/${companyId}?page=${targetPage}&limit=${targetPageSize}`;
      if (sortField) {
        url += `&sort_by=${sortField}`;
      }
      if (sortOrder) {
        url += `&order=${sortOrder}`;
      }
      const res = await clientFetch(url, {
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        notifyHttpError(res, { logMessage: "Error fetching departments." });
        setDepartmentList([]);
        setDepartmentPagination((prev) => ({
          ...prev,
          current: 1,
          total: 0,
        }));
        return;
      }

      const data = await res.json();
      const list = (Array.isArray(data) ? data : data.data ?? []) as Partial<DepartmentResponseType>[];
      const meta = Array.isArray(data) ? undefined : data.meta ?? {};

      setDepartmentList(list);
      setDepartmentPagination({
        current: meta?.page ?? targetPage,
        pageSize: meta?.limit ?? targetPageSize,
        total: meta?.total ?? list.length,
      });
    } catch (error) {
      notifyApiError({ error, logMessage: "Error fetching departments." });
      setDepartmentList([]);
      setDepartmentPagination((prev) => ({
        ...prev,
        current: 1,
        total: 0,
      }));
    } finally {
      setIsLoadingDepartments(false);
    }
  }, [companyId]);

  const fetchUsers = useCallback(async (page?: number, pageSize?: number, sortField?: string, sortOrder?: string) => {
    if (!companyId) {
      setUserList([]);
      setUserPagination({ current: 1, pageSize: pageSize ?? USER_PAGE_SIZE, total: 0 });
      setUserMeta({ active: 0 });
      setIsLoadingUsers(false);
      return;
    }

    setIsLoadingUsers(true);

    const targetPage = page ?? 1;
    const targetPageSize = pageSize ?? USER_PAGE_SIZE;

    let url = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/company/${companyId}?page=${targetPage}&limit=${targetPageSize}`;
    if (sortField && sortOrder) {
      url += `&sortField=${sortField}&sortOrder=${sortOrder}`;
    }
    try {
      const res = await clientFetch(url, {
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        notifyHttpError(res, { logMessage: "Error fetching users." });
        setUserList([]);
        setUserPagination((prev) => ({
          ...prev,
          current: 1,
          total: 0,
        }));
        setUserMeta({ active: 0 });
        return;
      }

      const data = await res.json();
      const list = (Array.isArray(data) ? data : data.data ?? []) as UserResponseType[];
      const meta = Array.isArray(data) ? undefined : data.meta ?? {};

      setUserList(list);
      setUserPagination({
        current: meta?.page ?? targetPage,
        pageSize: meta?.limit ?? targetPageSize,
        total: meta?.total ?? list.length,
      });
      setUserMeta({
        active: meta?.active ?? list.filter((user) => !user.is_disabled).length,
      });
    } catch (error) {
      notifyApiError({ error, logMessage: "Error fetching users." });
      setUserList([]);
      setUserPagination((prev) => ({
        ...prev,
        current: 1,
        total: 0,
      }));
      setUserMeta({ active: 0 });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [companyId]);

  useEffect(() => {
    let isCancelled = false;

    setDepartmentList([]);
    setDepartmentPagination({ current: 1, pageSize: DEPARTMENT_PAGE_SIZE, total: 0 });
    setUserList([]);
    setUserPagination({ current: 1, pageSize: USER_PAGE_SIZE, total: 0 });
    setCurrentAntdSortField(undefined);
    setCurrentAntdSortOrder(undefined);
    setCurrentDepartmentSortOrder(undefined);
    setCurrentDepartmentSortField(undefined);
    setUserMeta({ active: 0 });

    setIsPageReady(false);
    setIsLoadingCompany(true);
    setIsLoadingDepartments(true);
    setIsLoadingUsers(true);

    const validateAndLoadCompanyInfo = async () => {
      if (!companyId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
        return;
      }

      try {
        const companyResult = await refreshCompany(companyId);

        if (isCancelled) {
          return;
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return;
        }

        setIsLoadingCompany(false);
        setIsPageReady(true);
        fetchDepartments(1, DEPARTMENT_PAGE_SIZE);
        fetchUsers(1, USER_PAGE_SIZE);
      } catch (error) {
        console.error("Error validating company info.", error);
        if (isCancelled) {
          return;
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
      }
    };

    validateAndLoadCompanyInfo();

    return () => {
      isCancelled = true;
    };
  }, [companyId, fetchDepartments, fetchUsers, navigate, refreshCompany]);

  const handleDepartmentTableChange = useCallback((pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    const nextPage = pagination.current ?? 1;
    const nextPageSize = pagination.pageSize ?? DEPARTMENT_PAGE_SIZE;
    let sortOrder: 'asc' | 'desc' | undefined;
    let antdSortOrder: 'ascend' | 'descend' | undefined;
    let antdSortField: string | undefined;
    if (sorter && sorter.field && sorter.order) {
      antdSortOrder = sorter.order;
      antdSortField = sorter.field;
      if (antdSortOrder === 'ascend') {
        sortOrder = 'asc';
      } else if (antdSortOrder === 'descend') {
        sortOrder = 'desc';
      }
    }
    setCurrentDepartmentSortOrder(antdSortOrder);
    setCurrentDepartmentSortField(antdSortField);
    fetchDepartments(nextPage, nextPageSize, antdSortField, sortOrder);
  }, [fetchDepartments]);

  const handleUserTableChange = useCallback((pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    const nextPage = pagination.current ?? 1;
    const nextPageSize = pagination.pageSize ?? USER_PAGE_SIZE;

    let backendSortField: string | undefined;
    let backendSortOrder: string | undefined;
    let antdSortField: string | string[] | undefined;
    let antdSortOrder: 'ascend' | 'descend' | undefined;

    if (sorter && sorter.field && sorter.order) {
      antdSortField = sorter.field;
      antdSortOrder = sorter.order;

      if (Array.isArray(antdSortField) && antdSortField[0] === 'department' && antdSortField[1] === 'name') {
        backendSortField = 'departmentname';
      } else if (Array.isArray(antdSortField) && antdSortField[0] === 'department' && antdSortField[1] === 'brand_name') {
        backendSortField = 'brand_name';
      } else if (antdSortField === 'name') {
        backendSortField = 'username';
      } else {
        backendSortField = antdSortField as string;
      }

      if (antdSortOrder === 'ascend') {
        backendSortOrder = 'ASC';
      } else if (antdSortOrder === 'descend') {
        backendSortOrder = 'DESC';
      }
    }
    setCurrentAntdSortField(antdSortField);
    setCurrentAntdSortOrder(antdSortOrder);
    fetchUsers(nextPage, nextPageSize, backendSortField, backendSortOrder);
  }, [fetchUsers, setCurrentAntdSortField, setCurrentAntdSortOrder]);

  const formatNumber = useCallback((num: string) => {
    if (num.length === 11) {
      return `${num.slice(0, 3)}-${num.slice(3, 7)}-${num.slice(7)}`;
    }
    if (num.length === 10) {
      return `${num.slice(0, 4)}-${num.slice(4, 7)}-${num.slice(7)}`;
    }
    return num;
  }, []);

  const DATA_PLACEHOLDER = useMemo(() => {
    if (isLoadingCompany || !company) {
      return [];
    }
    return [
      {
        title: t("company.companyInfo"),
        column: [
          { key: "company_name", label: t("company.companyName"), value: company?.company_name ?? "" },
          { key: "address", label: t("company.address"), value: company?.address ?? "" },
          { key: "phone_number", label: t("company.phoneNumber"), value: formatNumber((company?.phone_number ?? "").toString()) },
          { key: "post_code", label: t("company.postCode"), value: company?.post_code ?? "" },
          { key: "toyoshima_sales_department", label: t("company.toyoshimaSalesDepartment"), value: company?.toyoshima_sales_department ?? "" },
          { key: "toyoshima_sales_rep_name", label: t("company.toyoshimaSalesRepName"), value: company?.toyoshima_sales_rep_name ?? "" },
        ],
      },
      {
        title: t("company.plannedSite"),
        column: [
          { key: "planned_site_name", label: t("company.siteName"), value: company?.planned_site_name ?? "" },
          { key: "planned_site_address", label: t("company.address"), value: company?.planned_site_address ?? "" },
          { key: "planned_site_phone", label: t("company.phoneNumber"), value: formatNumber((company?.planned_site_phone ?? "").toString()) },
          { key: "planned_site_post_code", label: t("company.postCode"), value: company?.planned_site_post_code ?? "" },
          { key: "planned_site_email", label: t("auth.email"), value: company?.planned_site_email ?? "" },
          { key: "planned_site_contact_name", label: t("company.contactName"), value: company?.planned_site_contact_name ?? "" },
          { key: "planned_site_contact_position", label: t("company.contactPosition"), value: company?.planned_site_contact_position ?? "" },
        ],
      },
      {
        title: t("company.contact"),
        column: [
          { key: "contact_department", label: t("company.departmentName"), value: company?.contact_department ?? "" },
          { key: "contact_name", label: t("company.name"), value: company?.contact_name ?? "" },
          { key: "contact_position", label: t("company.position"), value: company?.contact_position ?? "" },
          { key: "contact_email", label: t("auth.email"), value: company?.contact_email ?? "" },
          { key: "contact_phone", label: t("company.phoneNumber"), value: formatNumber((company?.contact_phone ?? "").toString()) },
        ],
      },
      {
        title: t("company.billingInfo"),
        column: [
          { key: "billing_department", label: t("company.departmentName"), value: company?.billing_department ?? "" },
          { key: "billing_contact", label: t("company.name"), value: company?.billing_contact ?? "" },
          { key: "blilling_position", label: t("company.position"), value: company?.billing_position ?? "" },
          { key: "billing_email", label: t("auth.email"), value: company?.billing_email ?? "" },
          { key: "billing_phone", label: t("company.phoneNumber"), value: formatNumber((company?.billing_phone ?? "").toString()) },
        ],
      },
      {
        title: t("company.contractInfo"),
        column: [
          { key: "contract_start_date", label: t("company.startDate"), value: company?.contract_start_date ?? "" },
          { key: "contract_end_date", label: t("company.endDate"), value: company?.contract_end_date ?? "" },
          {
            key: "contract_amount",
            label: t("company.amount"),
            value: new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY", minimumFractionDigits: 0 }).format(company?.contract_amount ?? 0),
          },
        ],
      },
      {
        title: t("company.memo"),
        column: [
          { key: "company_memo", label: t("company.memo"), value: company?.company_memo ?? "" },
        ],
      },
    ];
  }, [company, formatNumber, isLoadingCompany, t]);


  const onClickDepartment = useCallback((departmentId: string) => {
    if (!companyId) return;
    const result = PATH_INFO.DEPARTMENT_DETAIL.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", departmentId);
    navigate(destination);
  }, [companyId, navigate]);

  const onClickMeasurementsError = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.MEASUREMENTS_ERROR.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const onClickDeviceList = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.COMPANY_DEVICES_LIST.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const onClickMeasurements = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.COMPANY_MEASUREMENTS_LIST.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const getOnDepartmentCell = (record: { id: string }) => ({
    onClick: () => onClickDepartment(record.id),
    style: { cursor: "pointer" },
  });

  const onClickManage = useCallback((departmentId?: number, userId?: string) => {
    if (!companyId || !departmentId || !userId) return;
    const result = PATH_INFO.DEPARTMENT_USER_MANAGE.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", `${departmentId}`).replace(":userId", userId);
    const backTo = buildCompanyInfoPath(companyId);
    navigate(destination, { state: buildAdminUserNavigationState(backTo, backTo, true) });
  }, [companyId, navigate]);

  const onClickToggleStatus = useCallback(async (userId?: string) => {
    if (!userId) return;

    const originalState = userList.find((user) => user.id === userId)?.is_disabled;

    setUserList((prev) =>
      prev.map((user) =>
        user.id === userId ? { ...user, is_disabled: !user.is_disabled } : user
      )
    );

    try {
      await toggleUserDisabled(userId);
      await fetchUsers(userPagination.current, userPagination.pageSize);
    } catch {
      setUserList((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, is_disabled: originalState } : user
        )
      );
    }
  }, [fetchUsers, toggleUserDisabled, userList, userPagination]);

  const onClickUserRegister = useCallback(() => {
    if (!companyId) return;
    const result = PATH_INFO.DEPARTMENT_USER_MANAGE.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", "undefined").replace(":userId", "register");
    const backTo = buildCompanyInfoPath(companyId);
    navigate(destination, { state: buildAdminUserNavigationState(backTo, backTo, true) });
  }, [companyId, navigate]);

  const onClickUser = useCallback((departmentId?: number, userId?: string) => {
    if (!companyId || !userId) return;
    const result = PATH_INFO.DEPARTMENT_USER_DETAIL.PATH.replace(":companyId", companyId);
    const destination = result.replace(":departmentId", `${departmentId}`).replace(":userId", userId);
    const backTo = buildCompanyInfoPath(companyId);
    navigate(destination, { state: buildAdminUserNavigationState(backTo, undefined, true) });
  }, [companyId, navigate]);

  const getOnUserCell = useCallback((record: UserResponseType) => ({
    onClick: () => onClickUser(record.department.id, record.id),
    style: {
      background: record.is_disabled ? "rgba(0, 0, 0, 0.04)" : "",
      color: record.is_disabled ? "rgba(0,0,0,0.5)" : "",
      cursor: "pointer",
    },
  }), [onClickUser]);

  const onClickCompanyManage = useCallback(() => {
    if (!companyId) return;
    const destination = PATH_INFO.COMPANY_MANAGE.PATH.replace(":companyId", companyId);
    navigate(destination);
  }, [companyId, navigate]);

  const activeUserValue = userMeta.active;
  const totalUserValue = userPagination.total;

  const onUserUploadCsv = useCallback(async (options: any) => {
    const { file, onSuccess, onError } = options;

    if (!companyId) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/users/upload/company/${companyId}`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        Modal.success({ content: t("messages.uploadSucceeded", { fileName: file.name }), centered: true });
        await fetchUsers(userPagination.current, userPagination.pageSize);
        onSuccess(result);
      } else {
        const payload = await parseCsvErrorPayload(response);
        showCsvUploadErrorModal(file.name, payload);
        onError("error");
      }
    } catch (_error) {
      showCsvUploadErrorModal(file.name);
      onError("error");
    } finally {
      setIsUploading(false);
    }
  }, [companyId, fetchUsers, userPagination, t]);

  const onUploadCsv = useCallback(async (options: any) => {
    const { file, onSuccess, onError } = options;
    if (!companyId) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/upload/${companyId}`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        Modal.success({ content: t("messages.uploadSucceeded", { fileName: file.name }), centered: true });
        await refreshCompany(companyId);
        await fetchDepartments(1, departmentPagination.pageSize);
        onSuccess(result);
      } else {
        const payload = await parseCsvErrorPayload(response);
        showCsvUploadErrorModal(file.name, payload);
        onError("error");
      }
    } catch (error) {
      showCsvUploadErrorModal(file.name);
      onError("error");
    } finally {
      setIsUploading(false);
    }
  }, [companyId, departmentPagination.pageSize, fetchDepartments, refreshCompany, t]);

  const onDownloadUsersCsv = useCallback(async () => {
    if (!companyId) return;

    const extractFileName = (contentDispositionHeader?: string | null) => {
      if (!contentDispositionHeader) {
        return undefined;
      }

      try {
        const parsed = parseContentDisposition(contentDispositionHeader); // Content-Dispositionヘッダーを既存ライブラリで解析
        const filenameStar = typeof parsed.parameters?.["filename*"] === "string" ? parsed.parameters["filename*"] : undefined;
        if (filenameStar) {
          return filenameStar;
        }

        const rawName = typeof parsed.parameters?.filename === "string" ? parsed.parameters.filename : undefined;
        if (!rawName) {
          return undefined;
        }

        if (rawName.includes("%")) {
          // filename*（encoding付き）ではなく解析後の通常のrawNameで%が混在する場合のみ追加デコードを試行する
          try {
            return decodeURIComponent(rawName);
          } catch (_error) {
            return rawName;
          }
        }

        return rawName;
      } catch (_error) {
        return undefined;
      }
    };

    setIsDownloadingUsersCsv(true);
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${companyId}/users/template`, {
        method: "GET",
      });

      if (!res.ok) {
        throw new Error("Failed to download users CSV");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = res.headers.get("Content-Disposition");
      const fallbackName = `users-template.csv`;
      link.href = url;
      link.setAttribute("download", extractFileName(disposition) ?? fallbackName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading users CSV.", error);
      Modal.error({ content: t("messages.csvDownloadFailed"), centered: true });
    } finally {
      setIsDownloadingUsersCsv(false);
    }
  }, [companyId, t]);

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t("common.loadingWithDots")} />
      </Flex>
    );
  }

  return (
    <div className="mb-20">
      <Spin spinning={isUploading} tip={t("common.csvUploading")} size="large" fullscreen />
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(PATH_INFO.COMPANY_LIST.PATH)} className="mb-2 -ml-3">{t("common.back")}</Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">{t("company.detailPage")}</p>
        <Button variant="solid" color="primary" icon={<EditOutlined />} onClick={onClickCompanyManage}>{t("company.editCompanyDepartment")}</Button>
        <Button
          variant="outlined"
          color={company?.is_disabled ? "green" : "danger"}
          icon={company?.is_disabled ? <CaretRightFilled /> : <PauseOutlined />}
          onClick={() => {
            if (!companyId) return;
            toggleCompanyStatus(parseInt(companyId, 10));
          }}
        >
          {company?.is_disabled ? t("common.restore") : t("common.stop")}
        </Button>
      </div>
      <Divider />
      {isLoadingCompany ? (
        <Flex justify="center" align="center">
          <Spin size="large" tip='Loading...' />
        </Flex>
      ) : (
        <Row justify="center" align="top" className="w-full" gutter={[24, 24]}>
          {DATA_PLACEHOLDER.map((section) => (
            <Col xs={12} xl={6} key={section.title}>
              <List
                header={<div className="font-bold">{section.title}</div>}
                bordered
                dataSource={section.column}
                renderItem={(item) => (
                  section.title === t('company.memo') ? (
                    <div className='overflow-y-auto max-h-[235px]'>
                      <List.Item style={{ background: '#FFFFFF' }}>
                        <Descriptions column={1} size="small" layout="vertical">
                          <Descriptions.Item label={item.label} key={item.key}>
                            <p className={`m-0 pl-2 whitespace-pre-wrap ${item.value ? '' : 'text-black/25'}`}>{item.value || t('common.noData')}</p>
                          </Descriptions.Item>
                        </Descriptions>
                      </List.Item>
                    </div>
                  ) : (
                    <List.Item style={{ background: '#FFFFFF' }}>
                      <Descriptions column={1} size="small" layout="vertical">
                        <Descriptions.Item label={item.label} key={item.key}>
                          <p className={`m-0 pl-2 ${item.value ? '' : 'text-black/25'}`}>{item.value || t('common.noData')}</p>
                        </Descriptions.Item>
                      </Descriptions>
                    </List.Item>
                  )
                )}
                className={`overflow-hidden bg-[#FAFAFA] ${section.title === t('company.memo') ? 'max-h-[285px]' : ''}`}
              />
            </Col>
          ))}
        </Row>
      )}
      <Divider />
      <Flex className="pl-2 text-xl mb-2 lg:mb-0" justify="space-between">
        <Flex>
          <ProfileOutlined className="text-[#1677ff]" />
          <span className="pl-2">{t("company.departmentList")}</span>
        </Flex>
      </Flex>
      <Flex className="w-full flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <Flex align="center" className="order-2 ml-3 mb-1 lg:order-1">
          <Badge status="processing" color="blue" />
          <span className="pl-1">{t("company.departmentTotalCount", { count: departmentPagination.total })}</span>
        </Flex>
          <Flex gap='small' justify="end" className="order-1 mb-3 w-full justify-stretch sm:justify-end lg:order-2 lg:w-auto">
          <CsvImportModalButton
            title={t("csvImportTitles.departments")}
            templateUrl={`${process.env.PUBLIC_URL}/csv-templates/departments.csv`}
            templateFileName="departments_template.csv"
            guideUrl={`${process.env.PUBLIC_URL}/csv-guides/department-import.md`}
            uploadRequest={onUploadCsv}
            disabled={isUploading}
            uploading={isUploading}
            buttonText={t("company.departmentCsvBulkRegister")}
          />
         </Flex>
      </Flex>
      <Flex vertical justify="center" className="relative">
        {isLoadingDepartments ? (
          <Flex justify="center" align="center">
            <Spin size="large" tip='Loading...' />
          </Flex>
        ) : (
          <Table
            dataSource={departmentList}
            bordered
            pagination={{
              current: departmentPagination.current,
              pageSize: departmentPagination.pageSize,
              total: departmentPagination.total,
              position: ['bottomCenter'],
            }}
            onChange={handleDepartmentTableChange}
            rowKey={(record) => record.id || ''}
          >
            <Column
              title={t("company.departmentName")}
              dataIndex="name"
              key="name"
              onCell={getOnDepartmentCell}
              sorter={true}
              sortOrder={currentDepartmentSortField === 'name' ? currentDepartmentSortOrder : undefined}
            />
            <Column
              title={t("company.brandName")}
              dataIndex='brand_name'
              key='brand_name'
              onCell={getOnDepartmentCell}
              sorter={true}
              sortOrder={currentDepartmentSortField === 'brand_name' ? currentDepartmentSortOrder : undefined}
            />
            <Column
              title={t("company.departmentMemo")}
              dataIndex='memo'
              key='department_memo'
              className="whitespace-pre-wrap"
              onCell={getOnDepartmentCell}
              render={(text) => (
                <div className="h-[70px] overflow-y-auto">
                  {text}
                </div>
              )}
            />
          </Table>
        )}
      </Flex>
      <Divider />
      <Flex className="pl-2 text-xl mb-2 lg:mb-0" justify="space-between">
        <Flex>
          <ProfileOutlined className="text-[#1677ff]" />
          <span className="pl-2">{t("company.userList")}</span>
        </Flex>
      </Flex>
      <Flex className="w-full flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <Flex align="center" className="order-2 ml-3 mb-1 lg:order-1">
          <Badge status="success" />
          <span className="pl-1">{t("company.activeUserCount", { active: activeUserValue, total: totalUserValue })}</span>
        </Flex>
          <Flex gap='small' justify="end" className="order-1 mb-3 w-full justify-stretch sm:justify-end lg:order-2 lg:w-auto" wrap>
          <Button type="primary" icon={<UserAddOutlined />} onClick={onClickUserRegister} className="w-full sm:w-auto">{t("company.userRegister")}</Button>
          <CsvImportModalButton
            title={t("csvImportTitles.users")}
            templateUrl={`${process.env.PUBLIC_URL}/csv-templates/users.csv`}
            templateFileName="users_template.csv"
            guideUrl={`${process.env.PUBLIC_URL}/csv-guides/user-import.md`}
            uploadRequest={onUserUploadCsv}
            disabled={isUploading}
            uploading={isUploading}
            buttonText={t("company.userCsvBulkRegister")}
            className="w-full sm:w-auto"
            buttonClassName="w-full sm:w-auto"
          />
          <Button type="primary" icon={<DownloadOutlined />} onClick={onDownloadUsersCsv} loading={isDownloadingUsersCsv} className="w-full sm:w-auto">{t("common.downloadCsv")}</Button>
         </Flex>
      </Flex>
      <Flex vertical justify="center" className="relative">
        {isLoadingUsers ? (
          <Flex justify="center" align="center">
            <Spin size="large" tip='Loading...' />
          </Flex>
        ) : (
          <Table<UserResponseType>
            dataSource={userList}
            bordered
            pagination={{
              current: userPagination.current,
              pageSize: userPagination.pageSize,
              total: userPagination.total,
              position: ['bottomCenter'],
            }}
            onChange={handleUserTableChange}
            rowKey={(record) => record.id}
            scroll={{ x: 'max-content' }}
          >
            <Column
              title={t("company.departmentName")}
              dataIndex={['department', 'name']}
              key="department_name"
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={
                currentAntdSortField &&
                  Array.isArray(currentAntdSortField) &&
                  currentAntdSortField.length === 2 &&
                  currentAntdSortField[0] === 'department' &&
                  currentAntdSortField[1] === 'name'
                  ? currentAntdSortOrder
                  : undefined
              }
            />
            <Column
              title={t("company.position")}
              dataIndex='position'
              key='position'
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={currentAntdSortField === 'position' ? currentAntdSortOrder : undefined}
            />
            <Column
              title={t("company.name")}
              dataIndex='name'
              key='name'
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={currentAntdSortField === 'name' ? currentAntdSortOrder : undefined}
            />
            <Column title={t("auth.email")} dataIndex='email' key='email' onCell={getOnUserCell} />
            <Column
              title={t("company.brandName")}
              dataIndex={['department', 'brand_name']}
              key='brand_name'
              onCell={getOnUserCell}
              sorter={true}
              sortOrder={
                currentAntdSortField &&
                  Array.isArray(currentAntdSortField) &&
                  currentAntdSortField.length === 2 &&
                  currentAntdSortField[0] === 'department' &&
                  currentAntdSortField[1] === 'brand_name'
                  ? currentAntdSortOrder
                  : undefined
              }
            />
            <Column title='PIN' dataIndex='pin_code' key='pin_code' onCell={getOnUserCell} />
            <Column
              title={t("common.operation")}
              dataIndex='action'
              key='action'
              fixed='right'
              render={(_: any, record: UserResponseType) => (
                <Space size='middle'>
                  <Button variant='outlined' color='primary' onClick={() => onClickManage(record.department.id, record.id ?? '')}>{t("common.edit")}</Button>
                  <Button variant='outlined' color={record.is_disabled ? 'green' : 'danger'} onClick={() => onClickToggleStatus(record.id ?? '')}>{record.is_disabled ? t("common.restore") : t("common.stop")}</Button>
                </Space>
              )}
              width='175px'
            />
          </Table>
        )}
      </Flex>
      <Divider />
      <Flex gap="middle" align="stretch" justify="center" wrap className="w-full flex-col sm:flex-row">
        <Button type="primary" size="large" icon={<ReconciliationOutlined />} onClick={onClickMeasurements} className="w-full sm:w-auto">{t("company.measurementResults")}</Button>
        <Button type="primary" size="large" icon={<DatabaseOutlined />} onClick={onClickDeviceList} className="w-full sm:w-auto">{t("company.deviceList")}</Button>
        <Button type="primary" size="large" icon={<SlidersOutlined />} onClick={onClickMeasurementsError} className="w-full sm:w-auto">{t("company.measurementErrors")}</Button>
      </Flex>
    </div>
  );
}
